import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { message } from 'antd'
import { Button } from '@forwardflow/ui-v2'
import {
   CalendarCheckIcon,
   CheckCircle02Icon,
   ClockIcon,
   PhoneIcon,
   StopCircle04Icon,
   UsersIcon,
   VolumeMaxIcon,
   ZapIcon,
} from '@forwardflow/icons'
import { DashboardContainer } from '@forwardflow/components/Dashboard'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import CampaignService from '@forwardflow/services/CampaignService'
import PhoneNumberService from '@forwardflow/services/PhoneNumberService'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber.ts'
import { homePath, isFreeFlow, isSandbox, wizardPath } from '../dev/devTools'
import { Card, LinkButton, StepHeader } from '../shell/Card'
import { browserTimezone, hoursSentence, listSentence, plural } from '../shell/format'
import { IStarterCampaign } from '../steps/first-campaign/starter-campaigns'
import { stopItems } from '../steps/stage-mapping-explained'
import { AreaCodePicker } from '../steps/AreaCode'
import { fullName } from '../steps/you-and-team/seats'
import {
   FIRST_CAMPAIGN_NUMBERS,
   ILaunchDeps,
   LAUNCH_STAGES,
   LaunchError,
   launchFirstCampaign,
   TLaunchStage,
} from '../steps/start-calling'
import { CampaignGlimpse, IGlimpseRow } from './CampaignGlimpse'
import { HoursEditor } from './HoursEditor'
import { useLaunchedCampaigns } from './launched-store'
import { useCampaignVocabulary } from './useCampaignVocabulary'
import { useVoiceOptions } from './useVoiceOptions'
import { VoicePick } from './VoicePick'
import { ICampaignDraft, WhoToCall } from './WhoToCall'

type TStage = 'who' | 'voice' | 'numbers' | 'hours' | 'appointments' | 'launching'

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Writing a campaign: two questions on the right (who to call, which
 * voice), everything else already decided on the left and editable there
 * (area code, calling hours, appointment hours). "Start calling" creates
 * the campaign, reserves the numbers, creates the assistant and activates.
 */
export const CampaignComposer = () => {
   const navigate = useNavigate()
   const location = useLocation()
   const incoming = (location.state || {}) as { text?: string; starter?: IStarterCampaign }
   const { currentOrganization } = useAppStore()
   const {
      onboardingData,
      isLoadingOrInitialization,
      initializeOnboardingData,
      setCampaignConfiguration,
      setBusinessHours,
      setAppointmentTimeframe,
      setIsCampaignConfigured,
      goToStep,
   } = useOnboardingStoreV2()
   const {
      campaignConfiguration: config,
      businessHours,
      appointmentTimeframe,
      invitedUsers,
      stageMappings,
   } = onboardingData

   // On the real dashboard the setup record (area code, hours, voice) may not
   // be in memory yet; read it for this organization once.
   useEffect(() => {
      if (isSandbox() || !currentOrganization || isLoadingOrInitialization) return
      if (onboardingData.organizationId !== currentOrganization) {
         void initializeOnboardingData(currentOrganization)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentOrganization])

   const { vocab } = useCampaignVocabulary()
   const { all: voices, recommended: recommendedVoice, find } = useVoiceOptions()

   /* ---------- The two answers ---------- */

   const [draft, setDraft] = useState<ICampaignDraft | null>(null)
   const [stage, setStage] = useState<TStage>('who')
   const [returnTo, setReturnTo] = useState<TStage>('who')

   const assistant = config?.ai_assistants?.[0]
   const voice = find(assistant?.voice_id)
   const assistantName = assistant?.assistant_name ?? voice?.name ?? ''
   const setVoice = ({ voiceId, name }: { voiceId: string; name: string }) =>
      setCampaignConfiguration({ ai_assistants: [{ id: 1, voice_id: voiceId, assistant_name: name }] })
   useEffect(() => {
      if (!assistant?.voice_id && recommendedVoice)
         setVoice({ voiceId: recommendedVoice.voiceId, name: recommendedVoice.name })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [recommendedVoice?.voiceId])

   /* ---------- Everything already decided ---------- */

   const areaCodes = config?.area_codes || []
   const owner = invitedUsers?.find((u) => u.isOwner)
   const members = (invitedUsers || []).filter((u) => !u.isOwner)
   const stops = useMemo(() => stopItems(stageMappings || []), [stageMappings])
   const timezone = businessHours?.timezone || browserTimezone()

   const openEditor = (next: TStage) => {
      if (stage === 'who' || stage === 'voice') setReturnTo(stage)
      setStage(next)
   }
   const closeEditor = () => setStage(returnTo)

   const rows: IGlimpseRow[] = [
      {
         id: 'who',
         icon: <ZapIcon width={15} />,
         label: 'Who we call',
         value: draft
            ? draft.name
            : 'Not picked yet',
         sub: draft
            ? `${draft.count !== undefined ? `${formatNumber(draft.count)} leads · ` : ''}${draft.campaignType.includes('revive') ? 'older leads' : 'new leads as they arrive'}`
            : 'Answer this on the right',
         pending: !draft,
         active: stage === 'who',
         onEdit: draft && stage !== 'who' ? () => setStage('who') : undefined,
      },
      {
         id: 'voice',
         icon: <VolumeMaxIcon width={15} />,
         label: 'Voice',
         value: `${assistantName || voice?.name}${voice && assistantName && assistantName !== voice.name ? ` (${voice.name}'s voice)` : ''}`,
         sub: 'Introduces itself by this name',
         active: stage === 'voice',
         onEdit: stage !== 'voice' ? () => (draft ? setStage('voice') : undefined) : undefined,
      },
      {
         id: 'numbers',
         icon: <PhoneIcon width={15} />,
         label: 'Phone numbers',
         value: areaCodes.length
            ? `${FIRST_CAMPAIGN_NUMBERS} local numbers in ${areaCodes.map((c) => `(${c})`).join(', ')}`
            : `${FIRST_CAMPAIGN_NUMBERS} local numbers we pick for you`,
         sub: 'Bought when you start. First three included.',
         active: stage === 'numbers',
         onEdit: () => openEditor('numbers'),
      },
      {
         id: 'hours',
         icon: <ClockIcon width={15} />,
         label: 'When we call',
         value: hoursSentence(businessHours?.days),
         sub: timezone ? timezone.replace(/_/g, ' ') : undefined,
         active: stage === 'hours',
         onEdit: () => openEditor('hours'),
      },
      {
         id: 'appointments',
         icon: <CalendarCheckIcon width={15} />,
         label: 'When we book you',
         value: hoursSentence(appointmentTimeframe?.days),
         sub: 'Appointments only land in these hours',
         active: stage === 'appointments',
         onEdit: () => openEditor('appointments'),
      },
      {
         id: 'team',
         icon: <UsersIcon width={15} />,
         label: 'Who gets the leads',
         value: owner
            ? members.length
               ? `${fullName(owner)} + ${plural(members.length, 'teammate')}`
               : `${fullName(owner)} (you)`
            : 'You',
         sub: members.length ? 'Shared in turn' : undefined,
         onEdit: () => (isSandbox() ? (goToStep('you-and-team'), navigate(wizardPath())) : navigate('/settings/user-management')),
         editLabel: 'Manage',
      },
      {
         id: 'stops',
         icon: <StopCircle04Icon width={15} />,
         label: 'Leads we leave alone',
         value: stops.length
            ? listSentence(stops.slice(0, 3).map((s) => s.name)) + (stops.length > 3 ? ` +${stops.length - 3}` : '')
            : 'None set',
         onEdit: () => (isSandbox() ? (goToStep('stop-rules'), navigate(wizardPath())) : navigate('/settings/stage-mapping')),
         editLabel: 'Manage',
      },
   ]

   /* ---------- Launch ---------- */

   const [launchStage, setLaunchStage] = useState<TLaunchStage | null>(null)
   const [error, setError] = useState<LaunchError | null>(null)
   const [done, setDone] = useState(false)

   const deps: ILaunchDeps = isFreeFlow()
      ? {
           createCampaign: async () => {
              await wait(700)
              return { id: `sandbox-${Date.now()}` }
           },
           searchNumbers: async (code) => {
              await wait(600)
              return Array.from({ length: 3 }, (_, i) => ({ phoneNumber: `+1${code || '303'}555010${i}` }))
           },
           reserveNumber: async () => wait(150),
           createAssistants: async () => wait(500),
           activate: async () => {
              await wait(600)
              return { hasFailedPurchase: false }
           },
        }
      : {
           createCampaign: (input) =>
              CampaignService.createCustomActiveLeadSource(
                 input.campaignName,
                 { eligible_criteria: input.criteria },
                 input.campaignType,
                 { bufferTime: input.bufferTime },
                 true,
                 null,
                 input.leadTypes
              ) as Promise<{ id: string } | null | undefined>,
           searchNumbers: async (code) =>
              ((await PhoneNumberService.getAvailablePhoneNumbers({ page: 1, limit: 50, search: code })) ||
                 []) as never,
           reserveNumber: (campaignId, number) =>
              CampaignService.addCampaignPhoneNumber(campaignId, number as never),
           createAssistants: (campaignId, a) =>
              CampaignService.createOrganizationCampaignAssistantsInBulk(campaignId, [
                 {
                    assistantName: a.assistantName,
                    voiceId: a.voiceId,
                    description: a.description,
                    designation: 'Sales assistant',
                    avatar: a.avatar,
                    previewUrl: a.previewUrl,
                 },
              ]),
           activate: (campaignId) => CampaignService.activateCampaign(campaignId),
        }

   const launch = async () => {
      if (!draft || !voice) return
      setError(null)
      setStage('launching')
      setLaunchStage('campaign')
      try {
         const result = await launchFirstCampaign(
            {
               campaignName: draft.name,
               criteria: draft.criteria,
               campaignType: draft.campaignType,
               leadTypes: draft.leadTypes,
               bufferTime: draft.bufferTime,
               areaCodes,
               assistant: {
                  assistantName: assistantName || voice.name,
                  voiceId: voice.voiceId,
                  avatar: voice.avatar,
                  description: `Hello I am ${assistantName || voice.name}, your real estate assistant`,
                  previewUrl: voice.previewUrl,
               },
            },
            { ...deps, onStage: setLaunchStage }
         )
         if (!businessHours?.timezone && timezone) setBusinessHours({ ...businessHours, timezone })
         setCampaignConfiguration({
            campaign_name: draft.name,
            source: draft.source || '',
            campaign_filters: { eligible_criteria: draft.criteria },
            campaign_type: draft.campaignType,
            lead_type_filter: draft.leadTypes,
            first_to_contact: { buffer_time: draft.bufferTime },
            status: 'active',
         })
         setIsCampaignConfigured(true)
         useLaunchedCampaigns.getState().add({
            id: result.campaignId,
            name: draft.name,
            leads: draft.count,
            areaCodes,
            assistantName: assistantName || voice.name,
            voiceName: voice.name,
            launchedAt: Date.now(),
         })
         setDone(true)
         if (result.hasFailedPurchase) {
            message.warning('The campaign is live, but one number could not be bought. Check Phone numbers.')
         } else if (result.emptyAreaCodes.length) {
            message.info(`No numbers in (${result.emptyAreaCodes.join(', ')}); we used the next best area code.`)
         }
         navigate(homePath())
      } catch (e) {
         const err = e instanceof LaunchError ? e : new LaunchError(launchStage || 'campaign', "That didn't work. Try again.")
         setError(err)
         setLaunchStage(null)
         setStage('voice')
      }
   }

   const launching = stage === 'launching' && !done
   const stageIndex = LAUNCH_STAGES.findIndex((s) => s.id === launchStage)

   const stepLine =
      stage === 'who'
         ? 'Step 1 of 2 · Who to call'
         : stage === 'voice'
           ? 'Step 2 of 2 · The voice'
           : stage === 'launching'
             ? 'Starting your campaign'
             : 'Change a detail'

   return (
      <div className="w-full max-w-[1120px] mx-auto" data-testid="campaign-composer">
         <div className="flex items-center justify-between gap-4 py-4 md:py-5">
            <div className="min-w-0">
               <p className="text-white/45 text-xs">{stepLine}</p>
               <h1 className="text-white font-semibold text-xl leading-tight">
                  {draft ? draft.name : 'New campaign'}
               </h1>
            </div>
            <LinkButton onClick={() => navigate(homePath())} disabled={launching}>
               Back to home
            </LinkButton>
         </div>

         <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-5 lg:gap-7 items-start">
            <div className="order-2 lg:order-1 lg:sticky lg:top-4">
               <CampaignGlimpse title="Your campaign" rows={rows} />
            </div>

            <section className="order-1 lg:order-2 min-w-0 space-y-4" data-testid="composer-panel">
               {error && (
                  <Card className="border-[#9F7BFF]/40" testId="launch-error">
                     <p className="text-white font-medium text-sm">{error.message}</p>
                     <p className="text-[#99A1AF] text-xs">
                        {error.stage === 'numbers'
                           ? 'Pick another area code on the left, or finish in the campaign console where you can choose any number.'
                           : 'Nothing was charged twice. Try again, or finish in the campaign console.'}
                     </p>
                     {error.campaignId && !isSandbox() && (
                        <LinkButton
                           onClick={() =>
                              navigate(
                                 `/campaign/${encodeURIComponent(error.campaignId!)}/phone-number?campaignName=${encodeURIComponent(draft?.name || '')}`
                              )
                           }
                        >
                           Finish in the campaign console
                        </LinkButton>
                     )}
                  </Card>
               )}

               {stage === 'who' && (
                  <WhoToCall
                     vocab={vocab}
                     initialText={incoming.text}
                     starter={incoming.starter}
                     initial={draft}
                     onDone={(d) => {
                        setDraft(d)
                        setStage('voice')
                     }}
                  />
               )}

               {stage === 'voice' && voice && (
                  <VoicePick
                     voices={voices}
                     selectedId={voice.voiceId}
                     name={assistantName}
                     onChange={setVoice}
                     onLaunch={() => void launch()}
                     launchLabel={
                        draft?.count !== undefined
                           ? `Start calling ${formatNumber(draft.count)} leads`
                           : 'Start calling'
                     }
                     launching={launching}
                     onBack={() => setStage('who')}
                  />
               )}

               {stage === 'numbers' && (
                  <Card testId="numbers-editor">
                     <StepHeader
                        title="Which area code should your numbers have?"
                        lede="Leads see a caller from this area. We buy the numbers when the campaign starts."
                     />
                     <AreaCodePicker
                        selectedCodes={areaCodes}
                        onChange={(codes) => setCampaignConfiguration({ area_codes: codes })}
                     />
                     <Button size="middle" className="!h-10" onClick={closeEditor} disabled={!areaCodes.length}>
                        Done
                     </Button>
                  </Card>
               )}

               {stage === 'hours' && (
                  <Card testId="hours-editor-card">
                     <StepHeader
                        title="When should we call your leads?"
                        lede="Calls and texts only go out in these hours. Leads who ask to be called later are called later."
                     />
                     <HoursEditor
                        days={businessHours?.days || []}
                        sameForAllDays={businessHours?.isSameForAllDays ?? true}
                        timezone={timezone}
                        onChange={(days, isSameForAllDays) =>
                           setBusinessHours({ ...businessHours, days, isSameForAllDays, timezone })
                        }
                     />
                     <Button size="middle" className="!h-10" onClick={closeEditor}>
                        Done
                     </Button>
                  </Card>
               )}

               {stage === 'appointments' && (
                  <Card testId="appointments-editor-card">
                     <StepHeader
                        title="When can we book appointments for you?"
                        lede="When a lead is interested and you can't take the call, we book a time inside these hours."
                     />
                     <HoursEditor
                        days={appointmentTimeframe?.days || []}
                        sameForAllDays={appointmentTimeframe?.isSameForAllDays ?? true}
                        timezone={appointmentTimeframe?.timezone || timezone}
                        onChange={(days, isSameForAllDays) =>
                           setAppointmentTimeframe({
                              ...appointmentTimeframe,
                              days,
                              isSameForAllDays,
                              timezone: appointmentTimeframe?.timezone || timezone,
                           })
                        }
                     />
                     <Button size="middle" className="!h-10" onClick={closeEditor}>
                        Done
                     </Button>
                  </Card>
               )}

               {stage === 'launching' && (
                  <Card testId="launch-progress">
                     <StepHeader
                        title={done ? 'Your campaign is live' : 'Starting your campaign'}
                        lede={done ? 'Taking you home.' : 'About twenty seconds. Nothing to do.'}
                     />
                     <div className="space-y-2.5">
                        {LAUNCH_STAGES.map((s, i) => {
                           const state = done || i < stageIndex ? 'done' : i === stageIndex ? 'now' : 'next'
                           return (
                              <div key={s.id} className="flex items-center gap-3 text-sm">
                                 {state === 'done' ? (
                                    <CheckCircle02Icon width={18} height={18} color="#00D492" />
                                 ) : (
                                    <span
                                       className={cn(
                                          'w-[18px] h-[18px] rounded-full border-2',
                                          state === 'now'
                                             ? 'border-[#A684FF] border-t-transparent animate-spin'
                                             : 'border-white/15'
                                       )}
                                    />
                                 )}
                                 <span className={cn(state === 'next' ? 'text-white/40' : 'text-white')}>{s.label}</span>
                              </div>
                           )
                        })}
                     </div>
                  </Card>
               )}
            </section>
         </div>
      </div>
   )
}

/** The composer inside the real dashboard chrome, at `/campaign/new`. */
export const CampaignComposerPage = () => (
   <DashboardContainer>
      <CampaignComposer />
   </DashboardContainer>
)
